import { SystemError } from '../../errors';
import type { Field, Scalar } from './predicate';

export interface Boost<TDoc> {
  readonly field: Field<TDoc>;
  readonly value: Scalar | null | undefined;
  readonly weight: number;
}

type StringKey<TValue> = Extract<keyof TValue, string>;

type AlgoliaConfigChildPath<TValue> = NonNullable<TValue> extends readonly (infer Element)[]
  ? NonNullable<Element> extends object
    ? AlgoliaConfigPath<NonNullable<Element>>
    : never
  : NonNullable<TValue> extends object
    ? AlgoliaConfigPath<NonNullable<TValue>>
    : never;

/** Static Algolia config path whose root and nested segments exist on the document. */
export type AlgoliaConfigPath<TDoc> = {
  readonly [Key in StringKey<TDoc>]:
    | readonly [Key]
    | (AlgoliaConfigChildPath<TDoc[Key]> extends infer ChildPath
        ? ChildPath extends readonly string[]
          ? readonly [Key, ...ChildPath]
          : never
        : never);
}[StringKey<TDoc>];

export interface AlgoliaSearchableAttribute<TDoc> {
  readonly path: AlgoliaConfigPath<TDoc>;
  readonly mode?: 'unordered';
}

export interface AlgoliaFacetAttribute<TDoc> {
  readonly path: AlgoliaConfigPath<TDoc>;
  readonly mode?: 'filterOnly' | 'searchable';
}

interface AlgoliaConfigProjection<TDoc> {
  readonly searchableAttributes: readonly AlgoliaSearchableAttribute<TDoc>[];
  readonly attributesForFaceting: readonly AlgoliaFacetAttribute<TDoc>[];
}

export interface AlgoliaCustomRanking<TDoc> {
  readonly path: AlgoliaConfigPath<TDoc>;
  readonly direction: 'asc' | 'desc';
}

export interface SearchDescriptor<TDoc> {
  /** Stable logical name; adapters map it to an index or collection. */
  readonly entity: string;
  readonly indexName: { readonly algolia: string; readonly typesense: string };
  /** The deduped root segments of `algolia.searchableAttributes`, in declaration order. */
  readonly searchable: readonly Field<TDoc>[];
  /** `filterable` is a superset of the provider's facet config, never the other way round — a boosted or policy-constrained field absent from `algolia.attributesForFaceting` stays visible here rather than going silently inert. */
  readonly filterable: readonly Field<TDoc>[];
  readonly defaults: { readonly hitsPerPage: number };
  readonly algolia?: AlgoliaConfigProjection<TDoc>;
  /** Ranking is deliberately not unified because provider models are not isomorphic. */
  readonly ranking: {
    readonly algolia?: { readonly customRanking?: readonly AlgoliaCustomRanking<TDoc>[] };
    readonly typesense?: { readonly sortBy?: string; readonly queryBy?: string };
  };
  /**
   * Attribute carrying the tenant id. An adapter MUST AND `eq(tenantField, <caller's tenant>)` onto
   * every request rather than trusting callers; `createFakeProvider` does so when given a tenant.
   */
  readonly tenantField: Field<TDoc>;
}

/** The Typesense `query_by` for a descriptor. Never derived from `searchable`: it names Algolia attributes that may not be string fields in the collection. */
export function typesenseQueryBy(descriptor: Pick<SearchDescriptor<never>, 'entity' | 'ranking'>): string {
  const queryBy = descriptor.ranking.typesense?.queryBy?.trim();
  if (!queryBy) {
    throw new SystemError({
      code: 'SEARCH_DESCRIPTOR_MISSING_TYPESENSE_QUERY_BY',
      userMessage: `Search descriptor "${descriptor.entity}" declares no Typesense queryBy.`,
      retryable: false,
    });
  }
  return queryBy;
}
